import type { VizGraph } from './types';

const PALETTE = [
  '#007BFF',
  '#04B600',
  '#AD72DB',
  '#FFB800',
  '#00DCBF',
  '#00D1FF',
  '#FFADE8',
  '#F19049',
  '#6CAC58',
  '#6899B0',
  '#A07FC5',
  '#4DAFA8',
  '#E05D5D',
  '#8D6E63',
];

/**
 * Colour per type, hashed from the name so a class keeps its colour across
 * reloads and datasets (Olisé assigns them in first-seen order, which shifts).
 */
export function colorFor(type: string): string {
  let h = 0;
  for (let i = 0; i < type.length; i++) h = (h * 31 + type.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function degrees(data: VizGraph): Map<string, number> {
  const degree = new Map<string, number>();
  data.links.forEach((l) => {
    degree.set(l.source, (degree.get(l.source) ?? 0) + 1);
    degree.set(l.target, (degree.get(l.target) ?? 0) + 1);
  });
  return degree;
}

function keepOnly(data: VizGraph, keep: Set<string>): VizGraph {
  return {
    nodes: data.nodes.filter((n) => keep.has(n.id)),
    links: data.links.filter((l) => keep.has(l.source) && keep.has(l.target)),
  };
}

/** The `n` most-connected nodes, so large graphs open legible. */
export function backbone(data: VizGraph, n: number): VizGraph {
  if (data.nodes.length <= n) return data;
  const degree = degrees(data);
  const keep = new Set(
    [...data.nodes]
      .sort((a, b) => (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0))
      .slice(0, n)
      .map((node) => node.id),
  );
  return keepOnly(data, keep);
}

/** Drop nodes with at most one link (single pass); `keepIds` always survive. */
export function dropLeaves(data: VizGraph, keepIds?: ReadonlySet<string>): VizGraph {
  const degree = degrees(data);
  const keep = new Set(
    data.nodes
      .filter((node) => (degree.get(node.id) ?? 0) > 1 || keepIds?.has(node.id))
      .map((node) => node.id),
  );
  return keepOnly(data, keep);
}

export function filterNodes(data: VizGraph, keep: (id: string) => boolean): VizGraph {
  return keepOnly(data, new Set(data.nodes.filter((n) => keep(n.id)).map((n) => n.id)));
}
